import React, { useCallback, useEffect, useState } from 'react'
import { Col, Form, FormGroup, Input, Label, ModalBody, Row } from 'reactstrap'
import { ValidationBoundary, ValidationState, Validator } from '@/components/validation'
import { BasicHeader } from '@/dialogs/common/basicHeader'
import { ConfirmFooter } from '@/dialogs/common/confirmFooter'
import ErrorBoundary from '@/components/ErrorBoundary'
import Resx from '@/components/Resx'
import context from '@/context'
import LoadingIndicator from '@/components/LoadingIndicator'
import { useLocalization } from '@/hooks/localization'
import { format } from '@/utils/StringUtilities'
import keys from 'lodash/keys'
import { DropDownList } from '@/components/dropDownList'
import { EnumDropDownList } from '@/components/enumDropDownList'
import { reportType } from '@/enums/reportType'
import { getStore } from '@/store'
import { dialogTypes } from '@/dialogs/index'
import { actionCreators as dialogActions } from '@/store/Dialogs'

const ReportTemplateImport = React.memo(function ReportTemplateImport (props) {
  const { fileAttachmentId, data, onAction } = props
  const { localize } = useLocalization()
  const [processing, setProcessing] = useState(true)
  const [importData, setImportData] = useState(data)
  const [selectedReportType, setReportType] = useState(0)
  const [category, setCategory] = useState(null)
  const categoryId = category && category.id
  const [error, setError] = useState(null)
  const onCategoryChange = useCallback(e => {
    setCategory(e.item)
  }, [])
  useEffect(() => {
    if (fileAttachmentId) {
      context.api.reportTemplateService.importTemplateFromFile(fileAttachmentId)
        .then(data => {
          setImportData(data)
          setProcessing(false)
        })
        .catch(err => {
          setError(getErrorInfo(err, localize))
          setProcessing(false)
        })
    }
  }, [fileAttachmentId, localize])

  useEffect(() => {
    if (data) {
      context.api.reportTemplateService.importTemplateFromIoDto(data)
        .then(response => {
          setImportData(response)
          setProcessing(false)
        })
        .catch(err => {
          setError(getErrorInfo(err, localize))
          setProcessing(false)
        })
    }
  }, [data, localize])

  const onSubmit = useCallback(() => {
    setProcessing(true)
    const request = { ...importData, reportType: selectedReportType ? selectedReportType.value : 0, categoryId }
    context.api.reportTemplateService.importTemplate(request)
      .then(result => {
        setProcessing(false)
        getStore().dispatch(dialogActions.openDialog({
          dialogType: dialogTypes.alert,
          title: 'Common:Confirmation',
          content: 'Common:SuccessfulImport'
        }))
        onAction()
      })
      .catch(err => {
        setError(getErrorInfo(err, localize))
        setProcessing(false)
      })
  }, [categoryId, importData, localize, onAction, selectedReportType])
  return (
    <ValidationBoundary>
      <BasicHeader title='Reporting.ReportTemplateImport' {...props} />
      <ModalBody>
        <ErrorBoundary>
          {processing ? (
            <LoadingIndicator />
          ) : (
            <Form className='report-template-import'>
              {error ? (
                <Row>
                  <Col sm='12'>
                    <Row>
                      <p>
                        <Resx name='Reporting.ReportTemplateImportErrors' />
                      </p>
                    </Row>
                    <Row>
                      {error}
                    </Row>
                  </Col>
                </Row>
              ) : (
                <React.Fragment>
                  <Row>
                    <Col sm='12'>
                      <p>
                        <Resx name='Reporting.ReportTemplateImportInstructions' />
                      </p>
                    </Col>
                  </Row>
                  {!!importData && <Row>
                    <Col sm='12'>
                      <FormGroup>
                        <Label>{localize('Reporting.TemplateCode')}</Label>
                        <Input disabled value={importData.templateCode} />
                        <small>{localize(importData.templateCodeExists ? 'Reporting.TemplateCodeExists' : 'Reporting.TemplateCodeIsNew')}</small>
                      </FormGroup>
                      <FormGroup>
                        <Label>{localize('Reporting.ColAxisCode')}</Label>
                        <Input disabled value={importData.colAxisCode} />
                        <small>{localize(importData.colAxisExists ? (importData.colAxisHasChanged ? 'Reporting.AxisHasChanged' : 'Reporting.AxisDidNotChange') : 'Reporting.NewAxis')}</small>
                      </FormGroup>
                      <FormGroup>
                        <Label>{localize('Reporting.RowAxisCode')}</Label>
                        <Input disabled value={importData.rowAxisCode} />
                        <small>{localize(importData.rowAxisExists ? (importData.rowAxisHasChanged ? 'Reporting.AxisHasChanged' : 'Reporting.AxisDidNotChange') : 'Reporting.NewAxis')}</small>
                      </FormGroup>
                    </Col>
                  </Row>}
                  <Row>
                    <Col sm='12'>
                      <FormGroup>
                        <Label>{localize('Reporting.ReportType')}</Label>
                        <EnumDropDownList enumDefinition={reportType} value={selectedReportType} onChange={setReportType} />
                      </FormGroup>
                      <FormGroup>
                        <Label>{localize('Reporting.Category')}</Label>
                        <Validator name='category' value={category} required>
                          <DropDownList dataSource={context.api.reportTemplateService.getCategories} value={category} onChange={onCategoryChange} />
                        </Validator>
                      </FormGroup>
                    </Col>
                  </Row>
                </React.Fragment>
              )}
            </Form>
          )}
        </ErrorBoundary>
      </ModalBody>
      <ValidationState render={({ isValid }) =>
        <ConfirmFooter
          primaryButtonText='Reporting.ImportTemplate'
          icon='import'
          {...props}
          disabled={!importData || !isValid}
          onAction={onSubmit} />}
      />
    </ValidationBoundary>
  )
})

function getErrorInfo (err, localize) {
  let error = err && err.response && err.response.data && err.response.data.error
  if (error) {
    if (error.validationErrors && error.validationErrors.length > 0) {
      const messages = error.validationErrors.map(x => format(localize(x.message), ...x.members))
        .reduce((g, x) => {
          g[x] = (g[x] || 0) + 1
          return g
        }, {})
      error = <p><ul>{keys(messages).map(k => <li>{messages[k]}x {k}</li>)}</ul></p>
    } else {
      error = <p>error.message</p>
    }
  } else {
    error = <pre>{JSON.stringify(err.response, null, 2)}</pre>
  }
  return error
}

export default {
  component: ReportTemplateImport
}



// WEBPACK FOOTER //
// ./src/dialogs/reportTemplateImport.js